import { getData } from '@maru44/huntre-utils/src/utils/data'
import {
  type CollectionReference,
  collection,
  type DocumentSnapshot,
  getCountFromServer,
  getDocs,
  limit,
  type OrderByDirection,
  orderBy,
  type QueryConstraint,
  query,
  startAfter,
} from 'firebase/firestore'
import { useCallback, useState } from 'react'
import { firestore } from 'src/utils/firebase'
import useSWR from './useSWR'

export type UsePaginatedCollectionOptions = {
  pageSize?: number
  orderByField?: string
  orderByDirection?: OrderByDirection
  additionalConstraints?: QueryConstraint[]
  disabled?: boolean
}

const DEFAULT_PAGE_SIZE = 20

type DocumentData = {
  id: string
  [field: string]: unknown
}

export const usePaginatedCollection = <T extends DocumentData>(collectionPath: string, options: UsePaginatedCollectionOptions = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, orderByField = 'createdAt', orderByDirection = 'desc', additionalConstraints = [], disabled = false } = options

  const [page, setPage] = useState(0)
  const [lastDocs, setLastDocs] = useState<DocumentSnapshot[]>([])

  const totalCount = useSWR(
    [`${collectionPath}/count`, collectionPath, ...additionalConstraints.map((c) => c.toString())],
    async () => {
      const col = collection(firestore, collectionPath)
      // additionalConstraintsがある場合はフィルタ付きでカウント
      const q = additionalConstraints.length > 0 ? query(col, ...additionalConstraints) : col
      const snapshot = await getCountFromServer(q)
      return snapshot.data().count
    },
    { disabled },
  )

  const list = useSWR(
    [collectionPath, page, ...additionalConstraints.map((c) => c.toString())],
    async () => {
      const col = collection(firestore, collectionPath) as CollectionReference
      const constraints: QueryConstraint[] = [orderBy(orderByField, orderByDirection), ...additionalConstraints, limit(pageSize)]

      if (page > 0 && lastDocs[page - 1]) {
        constraints.push(startAfter(lastDocs[page - 1]))
      }

      const q = query(col, ...constraints)
      const snap = await getDocs(q)

      // 最後のドキュメントを保存
      if (snap.docs.length > 0) {
        setLastDocs((prev) => {
          const newDocs = [...prev]
          newDocs[page] = snap.docs[snap.docs.length - 1]
          return newDocs
        })
      }

      return snap.docs.map((v) => getData<T>(v))
    },
    { disabled },
  )

  const goToNextPage = useCallback(() => {
    setPage((prev) => prev + 1)
  }, [])

  const goToPrevPage = useCallback(() => {
    setPage((prev) => Math.max(0, prev - 1))
  }, [])

  const goToPage = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const resetPage = useCallback(() => {
    setPage(0)
    setLastDocs([])
  }, [])

  return {
    data: list.data,
    isLoading: list.isLoading,
    error: list.error,
    mutate: list.mutate,
    totalCount: totalCount.data ?? 0,
    page,
    pageSize,
    goToNextPage,
    goToPrevPage,
    goToPage,
    resetPage,
  }
}
